/* eslint-disable @next/next/no-img-element */

import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "preact/hooks";
import {
  usePurchasedProductDetail,
  type Option,
  type PurchasedProductDetail,
  type SellingPlan,
  type VisibilityRule,
  type WidgetOptionConfig,
} from "@/hooks/usePurchasedProductDetail";
import { createCheckoutUrl, createSubscribeUrl } from "@/lib/checkoutUrl";
import { reactivateStayAiSubscription } from "@/lib/api";
import { getDiscountCode } from "@/lib/emailStorage";
import {
  getShopForTracking,
  trackBuyAgainWidgetInteract,
} from "@/lib/tracking";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toastSubscriptionApiError } from "./manage-subscription/subscription-errors";
import {
  getSellingPlansFromVariant,
  getPlanPricing,
  getPlanDisplayName,
  getPlanMonths,
  hasFreeShipping,
} from "@/lib/sellingPlanUtils";
import {
  findVariantBySelectedOptions,
  selectedOptionsFromVariant,
} from "@/lib/variant-utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Minus, Plus, Loader2, Trash2, X } from "lucide-react";
import { useCartContext } from "@/context/CartContext";
import { cn } from "@/lib/utils";

function valueShowsIn(
  cfg: WidgetOptionConfig | undefined,
  value: string,
  location: "main" | "buy_once",
): boolean {
  const v = cfg?.value_labels?.[value];
  if (v?.hidden) return false;
  if (location === "buy_once" && cfg?.buy_once_only) return true;
  const placement = v?.placement ?? "main";
  if (location === "main") return placement === "main" || placement === "both";
  return placement === "buy_once" || placement === "both";
}

interface WidgetBuyAgainSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: number | null;
  initialVariantId?: number | null;
  initialSellingPlanId?: string | null;
  onManageSubscription?: (productId: number) => void;
}

export function WidgetBuyAgainSheet({
  open,
  onOpenChange,
  productId,
  initialVariantId,
  initialSellingPlanId,
  onManageSubscription,
}: WidgetBuyAgainSheetProps) {
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, string>
  >({});
  // Per-option overrides for the buy-once card so clicks there don't move the
  // main selector. Sparse: only options the user explicitly clicked in the
  // buy-once card live here; the rest fall back to selectedOptions.
  const [buyOnceOverrides, setBuyOnceOverrides] = useState<
    Record<string, string>
  >({});
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<
    "buy_once" | string
  >("buy_once");
  const initializedForProductIdRef = useRef<number | null>(null);
  const [quantityLocal, setQuantityLocal] = useState(1);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" && window.innerWidth >= 768,
  );

  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    checkDesktop();
    window.addEventListener("resize", checkDesktop);
    return () => window.removeEventListener("resize", checkDesktop);
  }, []);

  const { data: product, isLoading } = usePurchasedProductDetail(
    productId,
    open,
  );

  const queryClient = useQueryClient();
  const reactivateMutation = useMutation({
    mutationFn: (subscriptionId: number) =>
      reactivateStayAiSubscription(subscriptionId),
    onSuccess: (_data, subscriptionId) => {
      if (product) {
        const shop = getShopForTracking([product as PurchasedProductDetail]);
        if (shop) {
          trackBuyAgainWidgetInteract("subscription_reactivate", shop, {
            productId: product.id,
            variantId: product.variant_id ?? undefined,
            subscription_contract_id: subscriptionId,
          });
        }
      }
      void queryClient.invalidateQueries({ queryKey: ["purchased-products"] });
      if (product?.id != null) {
        void queryClient.invalidateQueries({
          queryKey: ["purchased-product-detail", product.id],
        });
      }
      onOpenChange(false);
    },
    onError: (error) => {
      toastSubscriptionApiError(error);
    },
  });

  const {
    toggleCart,
    // getQuantity: getCartQuantity,
    items: cartItems,
  } = useCartContext();

  // Resolve the fallback value for an option: admin-configured fallback_value,
  // then first visible value, then the raw first value.
  const getFallbackValue = useCallback(
    (
      optionName: string,
      excludeHiddenValues?: Set<string>,
    ): string | undefined => {
      const optCfgs = product?.widget_config?.option_configs ?? [];
      const cfg = optCfgs.find((c) => c.name === optionName);
      const opt = product?.options?.find((o) => o.name === optionName);
      if (!opt) return undefined;
      // Admin-configured fallback takes priority
      if (cfg?.fallback_value) return cfg.fallback_value;
      // Otherwise first visible value (not statically hidden, not conditionally hidden)
      return (
        opt.values.find(
          (v) =>
            !cfg?.value_labels?.[v]?.hidden && !excludeHiddenValues?.has(v),
        ) ?? opt.values[0]
      );
    },
    [product?.widget_config?.option_configs, product?.options],
  );

  // Initialize selectedOptions, selectedDeliveryOption, and quantity from cart when product loads
  useEffect(() => {
    if (!open || !product?.variants?.length) return;
    if (initializedForProductIdRef.current === product.id) return;
    initializedForProductIdRef.current = product.id;
    const preferredVariantId =
      initialVariantId ?? product.variant_id ?? undefined;
    const initialVariant = preferredVariantId
      ? product.variants.find((v) => v.shopify_id === preferredVariantId)
      : undefined;
    const variant = initialVariant ?? product.variants[0];
    // Start from the purchased variant's options, then swap any hidden values
    // to their fallback so the initial selection is always visible.
    const initOptions = selectedOptionsFromVariant(variant);
    const optCfgs = product.widget_config?.option_configs ?? [];
    for (const opt of product.options ?? []) {
      const cfg = optCfgs.find((c) => c.name === opt.name);
      const currentVal = initOptions[opt.name];
      const isValueHidden = cfg?.value_labels?.[currentVal]?.hidden;
      if (isValueHidden) {
        initOptions[opt.name] = getFallbackValue(opt.name) ?? opt.values[0];
      }
    }
    setSelectedOptions(initOptions);
    setBuyOnceOverrides({});

    const initPlan =
      initialSellingPlanId &&
      initialVariant &&
      getSellingPlansFromVariant(initialVariant).some(
        (p) => p.shopify_id === initialSellingPlanId,
      )
        ? initialSellingPlanId
        : "buy_once";
    setSelectedDeliveryOption(initPlan);

    // Initialize quantity: use cart qty when selection matches cart, else 1
    const initVariantId = variant.shopify_id;
    const cartItem =
      cartItems.find((i) => i.productId === product.id) ??
      cartItems.find((i) => i.variantId === initVariantId);
    const matchesCart =
      cartItem != null &&
      cartItem.variantId === initVariantId &&
      (cartItem.sellingPlanId ?? undefined) ===
        (initPlan !== "buy_once" ? initPlan : undefined);
    setQuantityLocal(matchesCart && cartItem ? cartItem.quantity : 1);
  }, [
    open,
    product?.id,
    product?.variant_id,
    product?.variants,
    product?.options,
    product?.widget_config?.option_configs,
    initialVariantId,
    initialSellingPlanId,
    cartItems,
    getFallbackValue,
  ]);

  const selectedVariant = useMemo(() => {
    if (!product?.variants?.length) return null;
    const found = findVariantBySelectedOptions(
      product.variants,
      selectedOptions,
    );
    if (found) return found;
    return (
      product.variants.find((v) => v.shopify_id === product.variant_id) ??
      product.variants[0]
    );
  }, [product?.variants, product?.variant_id, selectedOptions]);

  // Effective option map for the buy-once flow — main selection with any
  // explicit per-option overrides from the buy-once card layered on top.
  const buyOnceOptions = useMemo(
    () => ({ ...selectedOptions, ...buyOnceOverrides }),
    [selectedOptions, buyOnceOverrides],
  );

  const buyOnceVariant = useMemo(() => {
    if (!product?.variants?.length) return selectedVariant;
    return (
      findVariantBySelectedOptions(product.variants, buyOnceOptions) ??
      selectedVariant
    );
  }, [product?.variants, buyOnceOptions, selectedVariant]);

  // The variant the user is actually about to buy — buy-once flow uses the
  // buy-once override variant; subscription flow stays on the main selector.
  const activeVariant =
    selectedDeliveryOption === "buy_once" ? buyOnceVariant : selectedVariant;

  const effectiveVariantId =
    activeVariant?.shopify_id ?? product?.variant_id ?? null;

  const cartItemForProduct =
    product != null
      ? (cartItems.find((i) => i.productId === product.id) ??
        cartItems.find((i) =>
          product.variants.some((v) => v.shopify_id === i.variantId),
        ))
      : undefined;

  const currentSellingPlanId =
    selectedDeliveryOption === "buy_once" ? undefined : selectedDeliveryOption;
  const cartSellingPlanId = cartItemForProduct?.sellingPlanId ?? undefined;

  const exactCartMatch =
    cartItemForProduct != null &&
    effectiveVariantId != null &&
    cartItemForProduct.variantId === effectiveVariantId &&
    (cartSellingPlanId || undefined) === (currentSellingPlanId || undefined);

  // const variantInCartWithDifferentPlan =
  //   cartItemForProduct != null &&
  //   effectiveVariantId != null &&
  //   cartItemForProduct.variantId === effectiveVariantId &&
  //   (cartSellingPlanId || undefined) !== (currentSellingPlanId || undefined);

  // const hasOtherVariantInCart =
  //   cartItemForProduct != null &&
  //   effectiveVariantId != null &&
  //   cartItemForProduct.variantId !== effectiveVariantId;

  const sortedOptions = useMemo((): Option[] => {
    if (!product?.options) return [];
    const optCfgs = product.widget_config?.option_configs ?? [];
    return [...product.options]
      .sort((a, b) => a.position - b.position)
      .filter((opt) => {
        const cfg = optCfgs.find((c) => c.name === opt.name);
        return !cfg?.hidden && !cfg?.buy_once_only;
      });
  }, [product?.options, product?.widget_config]);

  const buyOnceOnlyOptions = useMemo((): Option[] => {
    if (!product?.options) return [];
    const optCfgs = product.widget_config?.option_configs ?? [];
    return [...product.options]
      .sort((a, b) => a.position - b.position)
      .filter((opt) => {
        const cfg = optCfgs.find((c) => c.name === opt.name);
        if (cfg?.hidden) return false;
        if (cfg?.buy_once_only) return true;
        return opt.values.some((value) => valueShowsIn(cfg, value, "buy_once"));
      });
  }, [product?.options, product?.widget_config]);

  // When the currently active buy-once value for any option has
  // `hide_buy_once_options` set in the admin, the buy-once box renders without
  // any nested option chooser — "Buy once" just buys the selected variant.
  const hideBuyOnceNestedOptions = useMemo((): boolean => {
    const optCfgs = product?.widget_config?.option_configs ?? [];
    for (const opt of product?.options ?? []) {
      const activeValue = buyOnceOptions[opt.name];
      if (!activeValue) continue;
      const cfg = optCfgs.find((c) => c.name === opt.name);
      if (cfg?.value_labels?.[activeValue]?.hide_buy_once_options) {
        return true;
      }
    }
    return false;
  }, [
    product?.options,
    product?.widget_config?.option_configs,
    buyOnceOptions,
  ]);

  // Compute which option values are conditionally hidden based on visibility rules
  // and current selectedOptions. Returns a map of { optionName -> Set<hiddenValue> }.
  const conditionallyHiddenValues = useMemo((): Record<string, Set<string>> => {
    const rules: VisibilityRule[] =
      product?.widget_config?.visibility_rules ?? [];
    const result: Record<string, Set<string>> = {};
    for (const rule of rules) {
      if (selectedOptions[rule.when_option] === rule.when_value) {
        if (!result[rule.target_option]) {
          result[rule.target_option] = new Set();
        }
        for (const v of rule.hidden_values) {
          result[rule.target_option].add(v);
        }
      }
    }
    return result;
  }, [product?.widget_config?.visibility_rules, selectedOptions]);

  // Quantity: always use local state; +/- never auto-update cart (except trash which removes immediately)
  const quantity = quantityLocal;
  const setQuantityForAdd = (newVal: number) =>
    setQuantityLocal(Math.max(1, newVal));

  // const cartQuantity =
  //   effectiveVariantId != null ? getCartQuantity(effectiveVariantId) : 0;
  // const quantityDiffersFromCart =
  //   exactCartMatch &&
  //   effectiveVariantId != null &&
  //   quantityLocal !== cartQuantity;

  useEffect(() => {
    if (!open) {
      setSelectedOptions({});
      setBuyOnceOverrides({});
      setSelectedDeliveryOption("buy_once");
      setQuantityLocal(1);
      initializedForProductIdRef.current = null;
    }
  }, [open]);

  const handleOptionChange = (optionName: string, value: string) => {
    setSelectedOptions((prev) => {
      const next = { ...prev, [optionName]: value };
      // If any other option's currently-selected value is hidden by the new
      // selection, switch it to its fallback value.
      const rules: VisibilityRule[] =
        product?.widget_config?.visibility_rules ?? [];
      for (const rule of rules) {
        if (rule.when_option === optionName && rule.when_value === value) {
          const currentVal = next[rule.target_option];
          if (currentVal != null && rule.hidden_values.includes(currentVal)) {
            const fb = getFallbackValue(
              rule.target_option,
              new Set(rule.hidden_values),
            );
            if (fb != null) {
              next[rule.target_option] = fb;
            }
            // If no fallback found, keep the current value so variant
            // matching still works — the option just won't be visible in the UI.
          }
        }
      }
      // Ensure every product option has a value (restores keys that were
      // never set or lost after a rule stopped applying).
      if (product?.options) {
        for (const opt of product.options) {
          if (next[opt.name] == null) {
            next[opt.name] = getFallbackValue(opt.name) ?? opt.values[0];
          }
        }
      }
      return next;
    });
    // Only reset delivery to buy_once when changing a buy-once-only option;
    // regular option changes should preserve the current plan selection.
    const isBuyOnceOnlyOption = buyOnceOnlyOptions.some(
      (o) => o.name === optionName,
    );
    if (isBuyOnceOnlyOption) {
      setSelectedDeliveryOption("buy_once");
    }
  };

  // Click handler for the buy-once card's value buttons — sets an override
  // scoped to the buy-once flow so the main selector keeps its highlight.
  const handleBuyOnceOptionChange = (optionName: string, value: string) => {
    setBuyOnceOverrides((prev) => ({ ...prev, [optionName]: value }));
    setSelectedDeliveryOption("buy_once");
  };

  // Option names that are hidden or buy-once-only — ignored when matching
  // variants for selling plan aggregation.
  const hiddenOptionNames = useMemo((): Set<string> => {
    const result = new Set<string>();
    for (const cfg of product?.widget_config?.option_configs ?? []) {
      if (cfg.buy_once_only || cfg.hidden) result.add(cfg.name);
    }
    return result;
  }, [product?.widget_config?.option_configs]);

  // Reference variant for pricing (pinned to fallback for hidden options).
  const sellingPlanVariant = useMemo(() => {
    if (!product?.variants?.length || !hiddenOptionNames.size)
      return selectedVariant;
    const pinned = { ...selectedOptions };
    for (const name of hiddenOptionNames) {
      const fb = getFallbackValue(name, conditionallyHiddenValues[name]);
      if (fb) pinned[name] = fb;
    }
    return (
      findVariantBySelectedOptions(product.variants, pinned) ?? selectedVariant
    );
  }, [
    product?.variants,
    selectedOptions,
    selectedVariant,
    hiddenOptionNames,
    conditionallyHiddenValues,
    getFallbackValue,
  ]);

  // When admin has configured selling_plan_visibility_rules, plans are no
  // longer scoped to the selected variant — the admin controls what's shown
  // entirely via the rules, so we aggregate every plan across the product.
  const hasSellingPlanVisibilityRules =
    (product?.widget_config?.selling_plan_visibility_rules ?? []).length > 0;

  // Aggregate selling plans from ALL variants matching the selected regular
  // options — hidden/buy-once-only options are ignored so plans aren't limited
  // to one fallback variant.
  const aggregatedPlans = useMemo((): {
    plans: SellingPlan[];
    variantMap: Map<string, number>;
  } => {
    const emptyMap = new Map<string, number>();
    if (!product?.variants?.length) return { plans: [], variantMap: emptyMap };
    if (!hiddenOptionNames.size && !hasSellingPlanVisibilityRules)
      return {
        plans: getSellingPlansFromVariant(selectedVariant),
        variantMap: emptyMap,
      };
    const seen = new Set<string>();
    const plans: SellingPlan[] = [];
    const variantMap = new Map<string, number>();
    for (const v of product.variants) {
      const matches =
        hasSellingPlanVisibilityRules ||
        v.selected_options.every(
          (opt) =>
            hiddenOptionNames.has(opt.name) ||
            selectedOptions[opt.name] === opt.value,
        );
      if (!matches) continue;
      for (const plan of getSellingPlansFromVariant(v)) {
        if (!seen.has(plan.shopify_id)) {
          seen.add(plan.shopify_id);
          plans.push(plan);
          variantMap.set(plan.shopify_id, v.shopify_id);
        }
      }
    }
    // Prefer the currently-selected variant's copy of each plan so price and
    // checkout reflect the user's actual selection — not whichever variant
    // happened to come first in product.variants[].
    if (sellingPlanVariant) {
      for (const plan of getSellingPlansFromVariant(sellingPlanVariant)) {
        if (variantMap.has(plan.shopify_id)) {
          variantMap.set(plan.shopify_id, sellingPlanVariant.shopify_id);
        }
      }
    }
    return { plans, variantMap };
  }, [
    product?.variants,
    selectedOptions,
    selectedVariant,
    sellingPlanVariant,
    hiddenOptionNames,
    hasSellingPlanVisibilityRules,
  ]);

  const sellingPlans = aggregatedPlans.plans;
  // Maps each selling plan shopify_id to the variant that owns it
  const planToVariantId = aggregatedPlans.variantMap;

  // When a selling plan is selected, use the variant that actually owns that
  // plan (relevant when plans are aggregated from multiple variants).
  const checkoutVariantId =
    selectedDeliveryOption !== "buy_once"
      ? (planToVariantId.get(selectedDeliveryOption) ?? effectiveVariantId)
      : effectiveVariantId;

  const subscriptionStatus = product?.subscription_status;
  const statusLower = subscriptionStatus?.toLowerCase();
  const buyOnceVariantIds =
    product?.widget_config?.buy_once_variant_shopify_ids ?? [];
  const isBuyOnlyVariant =
    selectedVariant != null &&
    buyOnceVariantIds.includes(selectedVariant.shopify_id);
  // Aggregate per-variant selling plan exclusions across all matching variants
  const excludedPlanIds = useMemo((): Set<string> => {
    const exclusions =
      product?.widget_config?.variant_selling_plan_exclusions ?? [];
    if (!exclusions.length) return new Set();
    if (!hiddenOptionNames.size) {
      // No hidden options — check only the selected variant
      const rule = exclusions.find(
        (e) => Number(e.variant_shopify_id) === sellingPlanVariant?.shopify_id,
      );
      return new Set(rule?.excluded_selling_plan_shopify_ids ?? []);
    }
    // Aggregate exclusions from all variants matching regular options
    const result = new Set<string>();
    for (const v of product?.variants ?? []) {
      const matches = v.selected_options.every(
        (opt) =>
          hiddenOptionNames.has(opt.name) ||
          selectedOptions[opt.name] === opt.value,
      );
      if (!matches) continue;
      const rule = exclusions.find(
        (e) => Number(e.variant_shopify_id) === v.shopify_id,
      );
      for (const id of rule?.excluded_selling_plan_shopify_ids ?? []) {
        result.add(id);
      }
    }
    return result;
  }, [
    product?.widget_config?.variant_selling_plan_exclusions,
    product?.variants,
    selectedOptions,
    sellingPlanVariant,
    hiddenOptionNames,
  ]);
  // Selling plans hidden by option-based visibility rules
  const optionHiddenPlanIds = useMemo((): Set<string> => {
    const rules = product?.widget_config?.selling_plan_visibility_rules ?? [];
    const result = new Set<string>();
    for (const rule of rules) {
      if (selectedOptions[rule.when_option] === rule.when_value) {
        for (const id of rule.hidden_selling_plan_shopify_ids) {
          result.add(id);
        }
      }
    }
    return result;
  }, [product?.widget_config?.selling_plan_visibility_rules, selectedOptions]);
  const effectiveSellingPlans = isBuyOnlyVariant
    ? []
    : sellingPlans.filter(
        (p) =>
          !excludedPlanIds.has(p.shopify_id) &&
          !optionHiddenPlanIds.has(p.shopify_id),
      );
  const showPurchaseOptions = effectiveSellingPlans.length > 0;
  const isLastPurchasedVariant =
    effectiveVariantId != null &&
    (product?.variant_id === effectiveVariantId ||
      product?.variant_id === Number(effectiveVariantId));
  const isSubscriptionVariant =
    isLastPurchasedVariant &&
    (statusLower === "active" ||
      statusLower === "paused" ||
      statusLower === "cancelled");
  const canReactivateInPlace =
    statusLower === "cancelled" &&
    isLastPurchasedVariant &&
    product?.shopify_subscription_id != null;
  // Active/paused + subscription plan on purchased variant → Retextion manage.
  // Cancelled + same variant + subscription selected → reactivate the existing contract in place.
  // Cancelled + different variant → new subscription via checkout.
  const selectingSubscriptionOnActiveVariant =
    isSubscriptionVariant && selectedDeliveryOption !== "buy_once";
  const buttonAction = selectingSubscriptionOnActiveVariant
    ? statusLower === "cancelled"
      ? canReactivateInPlace
        ? "reactivate"
        : "subscribe"
      : "manage"
    : effectiveSellingPlans.length === 0
      ? "buy"
      : selectedDeliveryOption === "buy_once"
        ? "buy"
        : (statusLower === "active" || statusLower === "paused") &&
            isLastPurchasedVariant
          ? "manage"
          : "subscribe";
  const buttonLabel =
    buttonAction === "manage"
      ? product?.subscription_is_flexible === true
        ? "Get it now"
        : "Manage subscription"
      : buttonAction === "reactivate"
        ? "Re-subscribe"
        : buttonAction === "subscribe"
          ? "Subscribe now"
          : "Buy now";

  const handlePrimaryAction = () => {
    if (!product || !checkoutVariantId) return;

    if (buttonAction === "manage") {
      onManageSubscription?.(product.id);
      onOpenChange(false);
      return;
    }
    if (buttonAction === "reactivate") {
      if (product.shopify_subscription_id == null) return;
      reactivateMutation.mutate(product.shopify_subscription_id);
      return;
    }
    const discountCode = getDiscountCode();
    const shop = getShopForTracking(product ? [product] : []);
    if (buttonAction === "subscribe") {
      if (
        selectedDeliveryOption === "buy_once" ||
        !effectiveSellingPlans.some(
          (p) => p.shopify_id === selectedDeliveryOption,
        )
      )
        return;
      if (shop)
        trackBuyAgainWidgetInteract("subscribe_now", shop, {
          productId: product.id,
          variantId: checkoutVariantId,
        });
      const url = createSubscribeUrl(
        product as PurchasedProductDetail,
        checkoutVariantId,
        quantity,
        selectedDeliveryOption,
        discountCode,
      );
      window.location.assign(url);
      return;
    }
    if (shop)
      trackBuyAgainWidgetInteract("buy_now", shop, {
        productId: product.id,
        variantId: checkoutVariantId,
      });
    const checkoutUrl = createCheckoutUrl(
      product,
      checkoutVariantId,
      quantity,
      discountCode,
    );
    window.location.assign(checkoutUrl);
  };

  if (!productId) return null;

  // Price and discounted price for the currently active variant. The buy-once
  // flow has its own variant (via buyOnceOverrides) so these track the right
  // amount whether the user is buying once or subscribing.
  const variantPrice = activeVariant?.price ?? product?.price ?? 0;
  const variantDiscountedPrice = activeVariant
    ? (activeVariant.price_after_discount ?? null)
    : (product?.price_after_discount ?? null);
  // The buy-once card always shows the buy-once variant's price so it doesn't
  // jump when the user toggles between the subscription and buy-once radios.
  const buyOnceCardPrice = buyOnceVariant?.price ?? product?.price ?? 0;
  // Admin-configured base price for the buy-once variant — when set, replaces
  // the natural variant price as the strikethrough next to the buy-once price.
  const buyOnceCardBasePrice = (() => {
    const overrides = product?.widget_config?.variant_base_prices ?? [];
    if (!buyOnceVariant?.shopify_id || !overrides.length) return null;
    const match = overrides.find(
      (o) => Number(o.variant_shopify_id) === buyOnceVariant.shopify_id,
    );
    return match ? Number(match.base_price) : null;
  })();
  const buyOnceCardDiscountedPrice =
    buyOnceVariant?.price_after_discount ?? null;

  // Base price used for selling plan calculations — pinned to sellingPlanVariant
  // so buy-once-only option changes don't affect subscription pricing.
  const sellingPlanBasePrice = sellingPlanVariant?.price ?? product?.price ?? 0;
  const sellingPlanBaseDiscountedPrice = sellingPlanVariant
    ? (sellingPlanVariant.price_after_discount ?? null)
    : null;

  // Apply the promo code discount to any given price (e.g. a subscription-discounted price).
  // Uses the selling-plan variant's discount info so promo stays stable.
  const applyPromo = (price: number): number | null => {
    if (sellingPlanBaseDiscountedPrice == null) return null;
    const v = sellingPlanVariant;
    if (v?.discount_amount != null) {
      return Math.max(0, Math.round((price - v.discount_amount) * 100) / 100);
    }
    if (v?.discount_percentage != null) {
      return Math.round(price * (1 - v.discount_percentage / 100) * 100) / 100;
    }
    // No explicit amount/percentage — derive from the base price delta
    const savings = sellingPlanBasePrice - sellingPlanBaseDiscountedPrice;
    return Math.max(0, Math.round((price - savings) * 100) / 100);
  };

  const selectedPlan =
    buttonAction === "subscribe" && selectedDeliveryOption !== "buy_once"
      ? effectiveSellingPlans.find(
          (p: SellingPlan) => p.shopify_id === selectedDeliveryOption,
        )
      : null;
  // Use the owning variant's price for the selected plan
  const selectedPlanBasePrice = (() => {
    if (!selectedPlan) return sellingPlanBasePrice;
    const ownerVid = planToVariantId.get(selectedPlan.shopify_id);
    if (!ownerVid) return sellingPlanBasePrice;
    return (
      product?.variants?.find((v) => v.shopify_id === ownerVid)?.price ??
      sellingPlanBasePrice
    );
  })();
  const subscriptionPricing = selectedPlan
    ? getPlanPricing(selectedPlanBasePrice, selectedPlan)
    : null;
  // Admin override takes precedence over the plan's native price_adjustments
  // for the footer button price and cart line item — keeps it consistent with
  // what's shown on the plan card above.
  const selectedPlanOverride = selectedPlan
    ? product?.widget_config?.selling_plan_discount_overrides?.find(
        (o) => o.selling_plan_shopify_id === selectedPlan.shopify_id,
      )
    : undefined;
  const subscriptionPrice = selectedPlanOverride
    ? Math.round(
        selectedPlanBasePrice *
          (1 - Number(selectedPlanOverride.discount_percentage) / 100) *
          100,
      ) / 100
    : (subscriptionPricing?.discountedPrice ?? null);
  // Price after subscription discount AND promo code combined
  const subscriptionPriceWithPromo =
    subscriptionPrice != null ? applyPromo(subscriptionPrice) : null;

  // Final price driving the footer button, header summary, and cart meta.
  // All selling plan prices are per-delivery (no month multiplication needed).
  const currentPrice = selectedPlan
    ? (subscriptionPriceWithPromo ?? subscriptionPrice!)
    : (variantDiscountedPrice ?? variantPrice);

  const formatPlanDescription = (
    plan: SellingPlan,
  ): { servings: string; delivery: string } | null => {
    const months = getPlanMonths(plan);
    const baseServings =
      selectedVariant?.title?.match(/^\d+\s+servings/)?.[0] ??
      selectedVariant?.title?.split("|")[0]?.trim() ??
      "";
    // Variant titles encode per-delivery quantity as a number near the end —
    // either a bare count ("Low sugar / 60") or a count + suffix ("4 Fills 8oz
    // / 1-Pack"). For multi-month plans, scale that count by the plan's months
    // so the label shows the total per order, not per month.
    const servings = (() => {
      if (months <= 1) return baseServings;
      const m = baseServings.match(/^(.*?)(\d+)(\D*)$/);
      if (!m) return baseServings;
      const baseCount = parseInt(m[2], 10);
      if (isNaN(baseCount)) return baseServings;
      return `${m[1]}${baseCount * months}${m[3]}`;
    })();
    const rawFreq =
      plan.name.toLowerCase().replace(/^delivered\s+every\s+/, "") ??
      plan.options?.[0]?.value?.toLowerCase() ??
      "";
    const freq = months === 1 ? "monthly" : `every ${months} months`;
    const delivery = `delivered ${freq}`;
    return servings && rawFreq ? { servings, delivery } : null;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isDesktop ? "right" : "bottom"}
        className="tw:h-[90dvh] tw:max-h-svh tw:data-[side=bottom]:h-[90dvh] tw:data-[side=bottom]:max-h-[90dvh] tw:flex tw:flex-col tw:gap-0 tw:rounded-t-2xl md:tw:rounded-t-none md:tw:max-w-md md:tw:h-full md:tw:max-h-full tw:overflow-hidden"
        onOpenChange={onOpenChange}
        showCloseButton={false}
      >
        <SheetHeader className="tw:shrink-0 tw:border-b tw:border-[#FDEDEE]/50 tw:flex tw:flex-row tw:items-center tw:justify-between tw:pb-2 tw:pr-2">
          <SheetTitle style={{ color: "var(--so-text, #523D33)" }}>
            Change product
          </SheetTitle>
          <SheetClose
            data-slot="sheet-close"
            render={
              <Button
                variant="ghost"
                className="tw:p-0 tw:m-0"
                size="icon-sm"
              />
            }
          >
            <X />
          </SheetClose>
        </SheetHeader>

        {isLoading || !product ? (
          <div className="tw:flex-1 tw:flex tw:items-center tw:justify-center tw:py-8 tw:min-h-0">
            <Loader2
              className="tw:size-6 tw:animate-spin"
              style={{ color: "var(--so-text, #523D33)" }}
            />
          </div>
        ) : (
          <>
            <div className="tw:flex-1 tw:overflow-y-auto tw:min-h-0 tw:space-y-6 tw:px-4 tw:py-4">
              <div className="tw:flex tw:gap-4">
                <div className="tw:relative tw:w-20 tw:h-20 tw:bg-[#FDEDEE] tw:rounded-lg tw:shrink-0 tw:overflow-hidden">
                  <img
                    src={product.image_url}
                    alt={product.title}
                    className="tw:w-full tw:h-full tw:object-cover tw:rounded-lg"
                  />
                </div>
                <div className="tw:flex-1">
                  <h3
                    className="tw:font-medium tw:text-sm tw:line-clamp-2"
                    style={{ color: "var(--so-text, #523D33)" }}
                  >
                    {product.title}
                  </h3>
                  <div className="tw:flex tw:items-baseline tw:gap-1.5 tw:mt-1">
                    <p
                      className="tw:text-sm"
                      style={{ color: "var(--so-text, #523D33)" }}
                    >
                      $
                      {selectedDeliveryOption === "buy_once"
                        ? variantPrice
                        : sellingPlanBasePrice}
                    </p>
                  </div>
                </div>
              </div>

              {sortedOptions.length > 0 && (
                <div className="tw:flex tw:flex-col tw:gap-4">
                  {sortedOptions.map((option) => {
                    const optCfg = product?.widget_config?.option_configs?.find(
                      (c) => c.name === option.name,
                    );
                    return (
                      <div
                        key={option.id}
                        className="tw:flex tw:flex-col tw:gap-2"
                      >
                        <label
                          className="tw:text-sm tw:font-medium"
                          style={{ color: "var(--so-text, #523D33)" }}
                        >
                          {optCfg?.label ?? option.name}
                        </label>
                        <div className="tw:flex tw:flex-wrap tw:gap-2">
                          {option.values
                            .filter(
                              (value) =>
                                valueShowsIn(optCfg, value, "main") &&
                                !conditionallyHiddenValues[option.name]?.has(
                                  value,
                                ),
                            )
                            .map((value) => {
                              const isSelected =
                                selectedOptions[option.name] === value;
                              return (
                                <button
                                  key={value}
                                  type="button"
                                  onClick={() =>
                                    handleOptionChange(option.name, value)
                                  }
                                  className={`tw:h-auto tw:py-2 tw:px-4 tw:text-xs tw:whitespace-nowrap tw:border tw:font-semibold tw:cursor-pointer tw:transition-colors tw:rounded-lg tw:shrink-0 hover:tw:opacity-90 tw:inline-flex tw:flex-1 tw:justify-center ${isSelected ? "tw:text-[#523D33] tw:bg-[#FAC8CA] tw:border-transparent" : "tw:bg-white tw:text-[#555555] tw:border-[#E0E0E0]"}`}
                                >
                                  {optCfg?.value_labels?.[value]?.display ??
                                    value}
                                </button>
                              );
                            })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="tw:flex tw:flex-col tw:gap-3">
                {showPurchaseOptions &&
                  effectiveSellingPlans.map((plan) => {
                    const isSelected =
                      selectedDeliveryOption === plan.shopify_id;
                    // Use the owning variant's price for this plan so
                    // PERCENTAGE discounts are applied to the correct base.
                    const planOwnerVariantId = planToVariantId.get(
                      plan.shopify_id,
                    );
                    const planNaturalBasePrice = planOwnerVariantId
                      ? (product.variants.find(
                          (v) => v.shopify_id === planOwnerVariantId,
                        )?.price ?? sellingPlanBasePrice)
                      : sellingPlanBasePrice;
                    const planAdminBasePrice = (() => {
                      const overrides =
                        product?.widget_config?.selling_plan_base_prices ?? [];
                      const match = overrides.find(
                        (o) => o.selling_plan_shopify_id === plan.shopify_id,
                      );
                      return match ? Number(match.base_price) : null;
                    })();
                    const discountOverride =
                      product?.widget_config?.selling_plan_discount_overrides?.find(
                        (o) => o.selling_plan_shopify_id === plan.shopify_id,
                      );
                    // Native plan pricing is always relative to the variant's
                    // live price (that's what Shopify's price_adjustment is
                    // applied against on checkout). The admin base price only
                    // alters how the savings are presented to the customer.
                    const pricing = getPlanPricing(planNaturalBasePrice, plan);
                    const planDiscountedPrice = discountOverride
                      ? Math.round(
                          planNaturalBasePrice *
                            (1 -
                              Number(discountOverride.discount_percentage) /
                                100) *
                            100,
                        ) / 100
                      : pricing.discountedPrice;
                    const displayName = getPlanDisplayName(plan);
                    const freeShipping = hasFreeShipping(plan);
                    const planPriceWithPromo = applyPromo(planDiscountedPrice);
                    const finalPrice =
                      planPriceWithPromo ?? planDiscountedPrice;
                    // Effective base used for the strikethrough next to the
                    // plan price and for computing savings %.
                    const planEffectiveBasePrice =
                      planAdminBasePrice != null &&
                      planAdminBasePrice > finalPrice
                        ? planAdminBasePrice
                        : planNaturalBasePrice;
                    const combinedSavePercent =
                      planEffectiveBasePrice > 0
                        ? Math.round(
                            ((planEffectiveBasePrice - finalPrice) /
                              planEffectiveBasePrice) *
                              100,
                          )
                        : pricing.savePercent;
                    const computedBadgePercent = Math.max(
                      pricing.savePercent,
                      combinedSavePercent,
                    );
                    const promoPercent =
                      selectedVariant?.discount_percentage ??
                      (selectedVariant?.discount_amount != null &&
                      planNaturalBasePrice > 0
                        ? Math.round(
                            (selectedVariant.discount_amount /
                              planNaturalBasePrice) *
                              100,
                          )
                        : 0);
                    const badgePercent = discountOverride
                      ? Number(discountOverride.discount_percentage) +
                        promoPercent
                      : computedBadgePercent;
                    const desc = formatPlanDescription(plan);
                    const mostPopularIds =
                      product?.widget_config?.most_popular_selling_plan_ids ??
                      [];
                    const isMostPopular = mostPopularIds.includes(
                      plan.shopify_id,
                    );
                    return (
                      <div
                        key={plan.shopify_id}
                        className={`tw:rounded-lg tw:border tw:overflow-hidden tw:transition-colors ${
                          isSelected
                            ? "tw:border-[#FAC8CA]"
                            : "tw:border-[#FDEDEE]"
                        }`}
                      >
                        {isMostPopular && (
                          <div className="tw:bg-[#FAC8CA] tw:px-3 tw:py-1">
                            <span className="tw:text-xs tw:font-semibold tw:text-white">
                              Most Popular
                            </span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedDeliveryOption(plan.shopify_id)
                          }
                          className={`tw:w-full tw:flex tw:items-start tw:gap-3 tw:p-3 tw:text-left tw:transition-colors tw:cursor-pointer ${
                            isSelected
                              ? "tw:bg-[#FDEDEE]"
                              : "tw:bg-[#FDEDEE]/50 hover:tw:bg-[#FDEDEE]"
                          }`}
                          style={{ color: "var(--so-text, #523D33)" }}
                        >
                          <span
                            className={`tw:shrink-0 tw:mt-0.5 tw:w-4 tw:h-4 tw:rounded-full tw:border-2 tw:flex tw:items-center tw:justify-center ${
                              isSelected
                                ? "tw:border-[#523D33] tw:bg-[#523D33]"
                                : "tw:border-gray-400"
                            }`}
                          >
                            {isSelected && (
                              <span className="tw:w-1.5 tw:h-1.5 tw:rounded-full tw:bg-white" />
                            )}
                          </span>
                          <div className="tw:flex-1 tw:min-w-0">
                            <div className="tw:flex tw:items-center tw:gap-2 tw:flex-wrap">
                              <span className="tw:font-semibold tw:text-sm">
                                {displayName}
                              </span>
                              {badgePercent > 0 && (
                                <span className="tw:inline-flex tw:items-center tw:gap-1 tw:px-2 tw:py-1 tw:rounded tw:bg-[#FAC8CA] tw:text-[#523D33] tw:text-sm tw:font-semibold tw:leading-none">
                                  Save {badgePercent}%
                                </span>
                              )}
                            </div>
                            {desc && (
                              <p className="tw:text-xs tw:mt-0.5 tw:opacity-80">
                                <span className="tw:font-bold tw:text-[#523D33]">
                                  {desc.servings}
                                </span>
                                , {desc.delivery}
                              </p>
                            )}
                            {!desc && (
                              <p className="tw:text-xs tw:mt-0.5 tw:opacity-80">
                                {plan.name}
                              </p>
                            )}
                            {freeShipping && (
                              <p className="tw:text-xs tw:font-bold tw:mt-0.5 tw:text-[#523D33]">
                                Free Shipping
                              </p>
                            )}
                            <p className="tw:text-xs tw:font-bold tw:mt-0.5 tw:text-[#523d33]">
                              Pause or cancel anytime
                            </p>
                          </div>
                          <div className="tw:shrink-0 tw:text-right">
                            <span className="tw:font-bold tw:text-[16px] tw:block">
                              ${finalPrice.toFixed(2)}
                            </span>
                            <span className="tw:text-[16px] tw:opacity-70 tw:line-through tw:block">
                              ${planEffectiveBasePrice.toFixed(2)}
                            </span>
                          </div>
                        </button>
                      </div>
                    );
                  })}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedDeliveryOption("buy_once")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ")
                      setSelectedDeliveryOption("buy_once");
                  }}
                  className={`tw:w-full tw:p-3 tw:rounded-lg tw:text-left tw:transition-colors tw:border tw:cursor-pointer ${
                    selectedDeliveryOption === "buy_once"
                      ? "tw:bg-[#FDEDEE] tw:border-[#FAC8CA]"
                      : "tw:bg-[#FDEDEE]/50 tw:border-[#FDEDEE] hover:tw:bg-[#FDEDEE]"
                  }`}
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  <div className="tw:flex tw:items-start tw:gap-3">
                    <span
                      className={`tw:shrink-0 tw:w-4 tw:h-4 tw:rounded-full tw:border-2 tw:flex tw:items-center tw:justify-center ${
                        selectedDeliveryOption === "buy_once"
                          ? "tw:border-[#523D33] tw:bg-[#523D33]"
                          : "tw:border-gray-400"
                      }`}
                    >
                      {selectedDeliveryOption === "buy_once" && (
                        <span className="tw:w-1.5 tw:h-1.5 tw:rounded-full tw:bg-white" />
                      )}
                    </span>
                    <div className="tw:flex-1 tw:min-w-0">
                      <span className="tw:font-semibold tw:text-sm">
                        Buy once
                      </span>
                    </div>
                    <div className="tw:shrink-0 tw:text-right">
                      <span className="tw:font-bold tw:text-[16px] tw:block">
                        ${buyOnceCardDiscountedPrice ?? buyOnceCardPrice}
                      </span>
                      {(() => {
                        const livePrice =
                          buyOnceCardDiscountedPrice ?? buyOnceCardPrice;
                        const strikePrice =
                          buyOnceCardBasePrice != null &&
                          buyOnceCardBasePrice > livePrice
                            ? buyOnceCardBasePrice
                            : buyOnceCardDiscountedPrice != null
                              ? buyOnceCardPrice
                              : null;
                        return strikePrice != null ? (
                          <span className="tw:text-[16px] tw:opacity-70 tw:line-through tw:block">
                            ${strikePrice}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </div>

                  {!hideBuyOnceNestedOptions &&
                    buyOnceOnlyOptions.length > 0 && (
                      <div className="tw:mt-3 tw:flex tw:flex-col tw:gap-3 tw:pl-7">
                        {buyOnceOnlyOptions.map((option) => {
                          const optCfg =
                            product?.widget_config?.option_configs?.find(
                              (c) => c.name === option.name,
                            );
                          return (
                            <div
                              key={option.id}
                              className="tw:flex tw:flex-wrap tw:gap-2 tw:justify-center"
                            >
                              {option.values
                                .filter(
                                  (value) =>
                                    valueShowsIn(optCfg, value, "buy_once") &&
                                    !conditionallyHiddenValues[
                                      option.name
                                    ]?.has(value),
                                )
                                .map((value) => {
                                  const adminBadge =
                                    optCfg?.value_labels?.[value]?.badge;
                                  const badge = adminBadge
                                    ? adminBadge
                                    : (() => {
                                        const overrides =
                                          product?.widget_config
                                            ?.variant_base_prices ?? [];
                                        if (!overrides.length) return null;
                                        const pillVariant =
                                          findVariantBySelectedOptions(
                                            product.variants,
                                            {
                                              ...buyOnceOptions,
                                              [option.name]: value,
                                            },
                                          );
                                        if (!pillVariant) return null;
                                        const match = overrides.find(
                                          (o) =>
                                            Number(o.variant_shopify_id) ===
                                            pillVariant.shopify_id,
                                        );
                                        if (!match) return null;
                                        const base = Number(match.base_price);
                                        const live =
                                          pillVariant.price_after_discount ??
                                          pillVariant.price;
                                        if (!(base > live)) return null;
                                        const pct = Math.round(
                                          ((base - live) / base) * 100,
                                        );
                                        return pct > 0 ? `Save ${pct}%` : null;
                                      })();
                                  const isValSelected =
                                    buyOnceOptions[option.name] === value;
                                  return (
                                    <div
                                      key={value}
                                      className="tw:flex tw:flex-1 tw:flex-col tw:items-center tw:gap-1"
                                    >
                                      <span
                                        aria-hidden={!badge}
                                        className={cn(
                                          "tw:text-[10px] tw:px-2 tw:py-1 tw:rounded tw:font-semibold tw:leading-none tw:whitespace-nowrap",
                                          badge
                                            ? "tw:bg-[#FAC8CA]/80 tw:text-[#523D33]"
                                            : "tw:invisible",
                                        )}
                                      >
                                        {badge ?? " "}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleBuyOnceOptionChange(
                                            option.name,
                                            value,
                                          );
                                        }}
                                        className={`tw:py-2 tw:px-4 tw:w-full tw:whitespace-nowrap tw:border tw:font-semibold tw:cursor-pointer tw:transition-colors tw:rounded-full tw:shrink-0 hover:tw:opacity-90 ${isValSelected ? "tw:text-[#523D33] tw:bg-[#FAC8CA] tw:border-transparent" : "tw:bg-white tw:text-[#555555] tw:border-[#E0E0E0]"}`}
                                      >
                                        {optCfg?.value_labels?.[value]
                                          ?.display ?? value}
                                      </button>
                                    </div>
                                  );
                                })}
                            </div>
                          );
                        })}
                      </div>
                    )}
                </div>
              </div>

              {!selectingSubscriptionOnActiveVariant && (
                <div className="tw:flex tw:flex-col tw:gap-2">
                  <label
                    className="tw:text-sm tw:font-medium"
                    style={{ color: "var(--so-text, #523D33)" }}
                  >
                    Quantity
                  </label>
                  <div className="tw:flex tw:items-center tw:rounded-md tw:border tw:border-gray-200 tw:bg-white tw:overflow-hidden tw:touch-manipulation tw:w-fit">
                    <button
                      type="button"
                      onClick={() => {
                        // Trash at qty 1: remove from cart immediately
                        if (
                          exactCartMatch &&
                          effectiveVariantId != null &&
                          quantity <= 1
                        ) {
                          toggleCart(effectiveVariantId, 1);
                          return;
                        }
                        setQuantityForAdd(quantity - 1);
                      }}
                      disabled={!exactCartMatch && quantity <= 1}
                      className="tw:w-9 tw:h-9 tw:flex tw:items-center tw:justify-center tw:bg-white hover:tw:bg-gray-50 disabled:tw:opacity-40 disabled:tw:cursor-not-allowed tw:cursor-pointer tw:border-none tw:touch-manipulation"
                      aria-label={
                        exactCartMatch && quantity <= 1
                          ? "Remove from cart"
                          : "Decrease quantity"
                      }
                    >
                      {exactCartMatch && quantity <= 1 ? (
                        <Trash2
                          className="tw:size-3.5"
                          style={{ color: "var(--so-text, #523D33)" }}
                        />
                      ) : (
                        <Minus
                          className="tw:size-3.5"
                          style={{ color: "var(--so-text, #523D33)" }}
                        />
                      )}
                    </button>
                    <span
                      className="tw:min-w-6 tw:text-center tw:text-sm tw:font-medium tw:tabular-nums"
                      style={{ color: "var(--so-text, #523D33)" }}
                    >
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQuantityForAdd(quantity + 1)}
                      className="tw:w-9 tw:h-9 tw:flex tw:items-center tw:justify-center tw:bg-white hover:tw:bg-gray-50 tw:cursor-pointer tw:border-none tw:touch-manipulation"
                      aria-label="Increase quantity"
                    >
                      <Plus
                        className="tw:size-3.5"
                        style={{ color: "var(--so-text, #523D33)" }}
                      />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <SheetFooter className="tw:shrink-0 tw:border-t tw:border-[#FDEDEE]/50 tw:bg-background tw:flex-col tw:gap-2">
              <Button
                onClick={handlePrimaryAction}
                disabled={reactivateMutation.isPending}
                className={`tw:w-full tw:active:opacity-50 tw:rounded-md tw:border-0 tw:font-normal tw:transition-colors tw:duration-300 tw:ease-in-out tw:bg-[#FAC8CA] tw:text-[#523D33]`}
              >
                {reactivateMutation.isPending && (
                  <Loader2 className="tw:size-4 tw:animate-spin tw:shrink-0 tw:mr-1.5" />
                )}
                {buttonAction === "manage" || buttonAction === "reactivate"
                  ? buttonLabel
                  : `${buttonLabel} - $${(currentPrice * quantity).toFixed(2)}`}
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
